"use client";

import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";
import Link from "next/link";
import { RotateCcw } from "lucide-react";
import { NovilotMark } from "@/components/brand/NovilotMark";
import { recoverFromChunkError } from "@/lib/chunkRecovery";

/**
 * The 500, for anything that throws below the root layout.
 *
 * Note the prop is `unstable_retry`, not `reset`. This version of Next renamed
 * it; the old name still type-checks as an unused prop and the retry button
 * would simply do nothing, which is a worse failure than the one it is meant
 * to recover from — see node_modules/next/dist/docs/01-app/03-api-reference/
 * 03-file-conventions/error.md.
 *
 * `error.message` is only the real message for client-side throws. Server
 * errors arrive redacted with a `digest` instead, deliberately, so nothing
 * sensitive reaches the browser. The digest is shown because it is the string
 * that matches this crash to a line in the server logs — without it a support
 * conversation has nothing to go on.
 */
export default function Error({
  error,
  unstable_retry,
}: {
  error: Error & { digest?: string };
  unstable_retry: () => void;
}) {
  useEffect(() => {
    console.error("[app] route error:", error);
    // A chunk that 404s means this tab predates the current deploy, not that
    // the page is broken. Fetch the new one instead of showing a 500 whose
    // "Try again" would load the same missing file.
    if (recoverFromChunkError(error)) return;
    Sentry.captureException(error);
  }, [error]);

  return (
    <main className="flex-1 flex items-center justify-center px-6 py-16 bg-background">
      <div className="w-full max-w-md text-center">
        <NovilotMark className="w-8 h-8 text-accent mx-auto" />

        <p className="mt-8 text-5xl font-semibold tracking-tight tabular-nums text-muted-foreground/30">
          500
        </p>

        <h1 className="mt-3 text-xl font-semibold tracking-tight">Something went wrong</h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          This page failed to load. Your manuscript is saved — this is a display problem, not a
          data one. Trying again often works.
        </p>

        <div className="mt-7 flex items-center justify-center gap-2">
          <button
            onClick={unstable_retry}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-accent text-accent-foreground text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Try again
          </button>
          <Link
            href="/dashboard"
            className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            Go to your projects
          </Link>
        </div>

        {error.digest && (
          <p className="mt-8 text-[11px] text-muted-foreground/60">
            Reference <span className="font-mono">{error.digest}</span>
          </p>
        )}
      </div>
    </main>
  );
}
